import { ChangeEvent, useEffect, useRef, KeyboardEvent, useState } from 'react';
import classNames from 'classnames';
import BaseField, { BaseFieldProps, FieldState } from '../Field';
import './inputField.scss';
import Icon, { IconType } from '../../icon/Icon';

export interface InputfieldProps extends BaseFieldProps {
  type?: 'text' | 'number' | 'password' | 'email' | 'search';
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onButtonClick?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onSubmit?: () => void;
  onClear?: () => void;
  onClickOptionSearch?: (option: string) => void;
  placeholder?: string;
  value?: string;
  indeterminate?: boolean;
  unit?: string;
  unitPosition?: 'left' | 'right';
  icon?: IconType;
  submitLabel?: string;
  isLoading?: boolean;
  useAutocomplete?: boolean;
  autocompleteIsLoading?: boolean;
  clearLabel?: string;
  minLength?: number;
  searchResults?: Array<{ link?: string; label: string; value?: string }>;
}

const InputField = ({
  type = 'text',
  id,
  value,
  state,
  placeholder,
  indeterminate = false,
  unit,
  unitPosition,
  errorMessage,
  icon,
  submitLabel,
  isLoading,
  useAutocomplete = false,
  autocompleteIsLoading = false,
  clearLabel = '',
  minLength = 3,
  searchResults = [],
  onChange,
  onSubmit,
  onClear,
  onClickOptionSearch,
  ...props
}: InputfieldProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSubmit) {
      e.preventDefault();
      console.log('Enter key pressed, submitting form');
      onSubmit();
    }

    if (e.key === 'Escape' && onClear) {
      e.preventDefault();
      onClear();
    }
  };

  // const renderAddon = () => {
  //   if (children) return <div className="input-addon">{children}</div>;
  //   if (unit)
  //     return <span className={`unit-label ${unitPosition}`}>{unit}</span>;
  //   return null;
  // };

  return (
    <BaseField
      id={id}
      state={state}
      errorMessage={errorMessage} // pass explicitly
      {...props}
    >
      <>
        <div className="a-form-field__field">
          {/* <div className={classNames('input-with-unit', unitPosition)}>
            {unit && unitPosition === 'left' && (
              <span className="unit-label left">{unit}</span>
            )} */}
          {icon &&
            (!isLoading ? (
              <Icon iconType={icon} className="u-icon-tiny form-field__icon" />
            ) : (
              <Icon
                iconType={IconType.Restart}
                className="u-icon-tiny form-field__icon"
                rotate={true}
              />
            ))}
          <input
            ref={inputRef}
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={(e) => {
              handleKeyDown(e);
              props.onKeyDown?.(e);
            }}
            placeholder={placeholder}
            className={classNames(
              {
                'is-focus': state === FieldState.FOCUS,
                'is-focus-key': state === FieldState.FOCUS_KEYBOARD,
              },
              props.extraClasses
            )}
            aria-invalid={state === FieldState.INVALID}
            disabled={state === FieldState.DISABLED}
            aria-describedby={
              props.description || errorMessage ? `${id}-message` : undefined
            }
          />

          {type === 'search' &&
            (!autocompleteIsLoading ? (
              <Icon
                iconType={IconType.Search}
                className="u-icon-micro a-form-field__prefix"
              />
            ) : (
              <div className="u-spinner u-icon-micro a-form-field__prefix"></div>
            ))}

          {type === 'search' && value && value && value.length > 0 && (
            <button
              type="button"
              className="a-btn u-label-small a-form-field__clear"
              aria-label={clearLabel}
              title={clearLabel}
              onClick={onClear}
            >
              <Icon iconType={IconType.Close} className="u-icon-micro" />
            </button>
          )}

          {type === 'search' && submitLabel && (
            <button
              type="submit"
              className="a-link u-label-small a-form-field__submit u-white-space-nowrap u-mi-2"
              disabled={(value ?? '').length < minLength}
              onClick={onSubmit}
            >
              {submitLabel}
            </button>
          )}

          {searchResults && isFocused && searchResults.length > 0 && (
            <div className="input-search">
              <div className="input-search__autocomplete">
                <ul>
                  {searchResults.map((result, index) => (
                    <li
                      key={result.value ?? index}
                      className="input-search__autocomplete-li"
                      onMouseDown={() => {
                        result.link !== undefined &&
                          onClickOptionSearch?.(result.link);
                      }}
                    >
                      <a
                        className="input-search__autocomplete-item"
                        href={result.link ?? '#'}
                      >
                        <span className="u-truncate-multi">{result.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* {unit && unitPosition === 'right' && (
              <span className="unit-label right">{unit}</span>
            )}
          </div> */}
        </div>
      </>
    </BaseField>
  );
};

export default InputField;
